import { useState } from 'react';
import { Share2, FileSpreadsheet, ShieldAlert, Plus, Minus, Pencil, Trash2, UserPlus, Check } from 'lucide-react';
import { AdminCalendar } from './';
import { AddClassForm } from './';
import { ClassData, SignupData, NewClassForm } from '../../types';
import { formatDate, dateToKey, formatTime } from '../../helpers';
import { InfoModal, ConfirmModal } from '../common';

interface AdminDashboardProps {
    dates: Date[];
    selectedDay: Date;
    setSelectedDay: (date: Date) => void;
    classesByDate: Record<string, ClassData[]>;
    orphanedClasses: ClassData[];
    classesForSelectedDay: ClassData[];
    signupsByClassId: Record<string, SignupData[]>;
    onDeleteClassCheck: (cls: ClassData) => void;
    onEditClass: (cls: ClassData) => void;
    onAddClass: (newClass: NewClassForm) => Promise<void>;
    onShare: (type: 'date' | 'class', id: string, name: string) => void;
    onMaintenance: () => void;
    setView: (view: 'calendar') => void;
    onRegisterStudent: (cls: ClassData) => void;
    onUpdateSignupPaymentStatus: (signupId: string, newStatus: 'completed' | 'pending') => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
    dates, selectedDay, setSelectedDay, classesByDate, orphanedClasses, classesForSelectedDay,
    signupsByClassId, onDeleteClassCheck, onEditClass, onAddClass, onShare, onMaintenance, setView, onRegisterStudent,
    onUpdateSignupPaymentStatus
}) => {
    // Panel collapse/expand state - starts collapsed
    const [isAddClassExpanded, setIsAddClassExpanded] = useState(false);
    // Tracks if form has unsaved changes
    const [isAddClassDirty, setIsAddClassDirty] = useState(false);

    // Info modal for clipboard feedback (replaces alert())
    const [infoModal, setInfoModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        variant: 'success' | 'error' | 'info' | 'warning';
    }>({ isOpen: false, title: '', message: '', variant: 'info' });

    // Confirm modal for unsaved changes (replaces window.confirm())
    const [unsavedChangesModal, setUnsavedChangesModal] = useState<{
        isOpen: boolean;
        onConfirmAction: (() => void) | null;
    }>({ isOpen: false, onConfirmAction: null });

    /**
     * Shows styled confirm modal if form has unsaved changes.
     * Calls the action directly if form is clean.
     */
    const confirmNavigationIfDirty = (action: () => void): void => {
        if (isAddClassDirty) {
            setUnsavedChangesModal({ isOpen: true, onConfirmAction: action });
        } else {
            action();
        }
    };

    /**
     * Wraps setSelectedDay with dirty form confirmation.
     */
    const handleDateChange = (newDate: Date) => {
        confirmNavigationIfDirty(() => {
            setIsAddClassDirty(false);
            setSelectedDay(newDate);
        });
    };

    /**
     * Wraps setView('calendar') with dirty form confirmation.
     */
    const handleExitAdmin = () => {
        confirmNavigationIfDirty(() => {
            setIsAddClassDirty(false);
            setView('calendar');
        });
    };

    /**
     * Handles Cancel button from AddClassForm - collapses panel and resets dirty state.
     */
    const handleCancelAddClass = () => {
        setIsAddClassDirty(false);
        setIsAddClassExpanded(false);
    };

    /**
     * Wraps onAddClass to collapse panel after successful class creation.
     */
    const handleAddClass = async (newClass: NewClassForm) => {
        await onAddClass(newClass);
        setIsAddClassDirty(false);
        setIsAddClassExpanded(false);
    };

    const handleExportToSheets = () => {
        const headers = ['Date', 'Time', 'Class', 'Instructor', 'Student Name', 'Email', 'Payment'];
        const rows = classesForSelectedDay.flatMap(cls => {
            const attendees = signupsByClassId[cls.id] || [];
            const timeStr = `${formatTime(cls.startTime)} - ${formatTime(cls.endTime)}`;
            
            if (attendees.length === 0) {
                return [[
                    cls.date,
                    timeStr,
                    cls.name,
                    cls.instructor,
                    '-',
                    '-',
                    '-'
                ]];
            }
            
            return attendees.map(a => [
                cls.date,
                timeStr,
                cls.name,
                cls.instructor,
                `${a.firstName} ${a.lastName}`,
                a.email,
                a.paymentMethod === 'arrangement' ? 'Arrangement' : 'Venmo'
            ]);
        });
        
        const tsv = [headers, ...rows].map(row => row.join('\t')).join('\n');
        
        navigator.clipboard.writeText(tsv).then(() => {
            setInfoModal({
                isOpen: true,
                title: 'Copied!',
                message: 'Data copied to clipboard! Open Google Sheets and paste (Cmd+V).',
                variant: 'success'
            });
        }).catch(err => {
            console.error('Failed to copy', err);
            setInfoModal({
                isOpen: true,
                title: 'Error',
                message: 'Failed to copy to clipboard.',
                variant: 'error'
            });
        });
    };

    return (
        <>
        <div className="grid md:grid-cols-12 gap-6 animate-in fade-in">
             <div className="col-span-12 lg:col-span-3">
                 <AdminCalendar 
                    dates={dates} 
                    selectedDay={selectedDay} 
                    setSelectedDay={handleDateChange} 
                    classesByDate={classesByDate} 
                 />
             </div>

             <div className="col-span-12 lg:col-span-9 space-y-8">
                 <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900 p-4 md:p-6 rounded-xl shadow-sm border border-slate-700">
                   <div className="min-w-0 w-full md:w-auto mb-4 md:mb-0">
                     <h2 className="text-2xl font-serif text-slate-200">Admin Console</h2>
                     
                     <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-slate-400 text-sm whitespace-nowrap">Managing:</span>
                        <input 
                           type="date"
                           className="bg-slate-800 border-none rounded text-slate-300 text-sm p-1 focus:ring-2 focus:ring-fuchsia-500 cursor-pointer appearance-none min-w-[120px]"
                           style={{ WebkitAppearance: 'none' }}
                           value={dateToKey(selectedDay)}
                           onChange={(e) => {
                               if(e.target.value) handleDateChange(new Date(e.target.value + "T12:00:00Z"));
                           }}
                        />
                        <button 
                            onClick={() => onShare('date', dateToKey(selectedDay), `${formatDate(selectedDay)}`)} 
                            className="text-slate-400 hover:text-slate-200 ml-2"
                        >
                            <Share2 size={16} />
                        </button>
                     </div>
                   </div>
                   <div className="flex flex-wrap gap-2 w-full md:w-auto">
                     <button onClick={handleExitAdmin} className="flex-1 md:flex-none bg-slate-700 px-4 py-2 rounded text-slate-200 hover:bg-slate-600 whitespace-nowrap">Exit Admin</button>
                     <button onClick={handleExportToSheets} className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap">
                       <FileSpreadsheet size={16} /> Copy for Sheets
                     </button>
                     <button onClick={onMaintenance} className="flex-1 md:flex-none bg-slate-800 text-white px-4 py-2 rounded border border-slate-600 hover:bg-slate-700 whitespace-nowrap">
                        Maintenance
                     </button>
                   </div>
                 </div>

                 {orphanedClasses.length > 0 && (
                    <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-4 animate-in fade-in slide-in-from-top-2">
                        <div className="flex items-center gap-2 text-rose-400 font-bold mb-2">
                            <ShieldAlert size={20} />
                            <h3>⚠️ {orphanedClasses.length} Orphaned Classes (No Date)</h3>
                        </div>
                        <div className="bg-slate-950/50 rounded max-h-40 overflow-y-auto p-2 mb-2">
                            {orphanedClasses.map(cls => (
                                <div key={cls.id} className="flex justify-between items-center text-sm p-1 border-b border-rose-900/50 last:border-0">
                                    <span className="text-slate-300">{cls.name || 'Untitled'} (ID: {cls.id.slice(0,6)}...)</span>
                                    <button 
                                        onClick={() => onDeleteClassCheck(cls)}
                                        className="text-rose-400 hover:text-rose-300 underline"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                 )}

                 <div className="bg-slate-900 p-4 md:p-6 rounded-xl shadow-sm border border-slate-700">
                    <h3 className="font-bold text-lg mb-4 text-slate-200 flex items-center gap-2">Classes on {formatDate(selectedDay)}</h3>
                    
                    {classesForSelectedDay.length === 0 ? (
                        <p className="text-slate-500 italic">No classes scheduled for this date.</p>
                    ) : (
                        <div className="space-y-4">
                            {classesForSelectedDay.map(cls => {
                                const attendees = signupsByClassId[cls.id] || [];
                                return (
                                  <div key={cls.id} className="bg-slate-800 p-4 rounded-xl border border-slate-600">
                                    <div className="flex justify-between items-start mb-3">
                                       <div>
                                          <h4 className="font-bold text-slate-100">{cls.name}</h4>
                                          <span className="text-xs text-slate-400 block sm:inline">
                                              {formatTime(cls.startTime)} - {formatTime(cls.endTime)} • {attendees.length}/{cls.capacity} booked
                                              {cls.paymentType === 'venmo' && <span className="ml-2 text-emerald-400">($)</span>}
                                          </span>
                                       </div>
                                       <div className="flex gap-2 ml-2">
                                            <button 
                                              type="button"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  onEditClass(cls);
                                              }} 
                                              className="text-blue-400 hover:text-blue-300 bg-slate-700 hover:bg-slate-600 p-2 rounded transition-colors"
                                            >
                                                <Pencil size={18} />
                                            </button>
                                            <button 
                                              type="button"
                                              title="Register Student Manually"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  onRegisterStudent(cls);
                                              }} 
                                              className="text-fuchsia-400 hover:text-fuchsia-300 bg-slate-700 hover:bg-slate-600 p-2 rounded transition-colors"
                                            >
                                                <UserPlus size={18} />
                                            </button>
                                            <button 
                                              type="button"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  onDeleteClassCheck(cls);
                                              }} 
                                              className="text-rose-400 hover:text-rose-300 bg-slate-700 hover:bg-slate-600 p-2 rounded transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                       </div>
                                    </div>
                                    <div className="bg-slate-950 rounded p-2 max-h-40 overflow-y-auto">
                                      {attendees.length === 0 ? (
                                        <p className="text-xs text-slate-500 italic">No signups yet</p>
                                      ) : (
                                        <ul className="text-sm space-y-1">
                                          {attendees.map(a => {
                                            const isPaid = a.paymentStatus === 'completed';
                                            
                                            return (
                                            <li key={a.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-slate-700 last:border-0 pb-1 gap-1">
                                              <div>
                                                <span>{a.firstName} {a.lastName}</span>
                                                <span className="text-slate-400 text-xs ml-2">
                                                    {a.email} 
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                 {isPaid ? (
                                                     <>
                                                         <span className="flex items-center gap-1 text-emerald-400 text-xs bg-emerald-900/30 px-2 py-0.5 rounded border border-emerald-900/50">
                                                            <Check size={12} /> Paid
                                                         </span>
                                                         <button 
                                                            onClick={() => onUpdateSignupPaymentStatus(a.id, 'pending')}
                                                            className="text-xs text-slate-500 hover:text-slate-300 underline ml-2"
                                                            title="Mark as Unpaid"
                                                         >
                                                            Mark Unpaid
                                                         </button>
                                                     </>
                                                 ) : (
                                                     <>
                                                         {/* Show Venmo or Arrangement based on payment method */}
                                                         {a.paymentMethod === 'arrangement' ? (
                                                             <span className="flex items-center gap-1 text-amber-400 text-xs bg-amber-900/30 px-2 py-0.5 rounded border border-amber-900/50">
                                                                Arrangement
                                                             </span>
                                                         ) : (
                                                             <span className="flex items-center gap-1 text-blue-400 text-xs bg-blue-900/30 px-2 py-0.5 rounded border border-blue-900/50">
                                                                Venmo
                                                             </span>
                                                         )}
                                                         <button 
                                                            onClick={() => onUpdateSignupPaymentStatus(a.id, 'completed')}
                                                            className="text-xs bg-slate-700 hover:bg-emerald-700 hover:text-white px-2 py-1 rounded transition-colors"
                                                            title="Mark as Paid"
                                                         >
                                                            Mark Paid
                                                         </button>
                                                     </>
                                                 )}
                                              </div>
                                            </li>
                                          )})}
                                        </ul>
                                      )}
                                    </div>
                                  </div>
                                )
                            })}
                        </div>
                    )}
                 </div>

                 <div className="bg-slate-900 p-4 md:p-6 rounded-xl shadow-sm border border-slate-700">
                    <button 
                        type="button"
                        onClick={() => setIsAddClassExpanded(!isAddClassExpanded)}
                        className="w-full font-bold text-lg text-slate-200 flex items-center gap-2 hover:text-fuchsia-400 transition-colors text-left"
                        aria-expanded={isAddClassExpanded}
                    >
                        {isAddClassExpanded ? (
                            <Minus size={20} className="text-fuchsia-500"/>
                        ) : (
                            <Plus size={20} className="text-fuchsia-500"/>
                        )}
                        Add Class to {formatDate(selectedDay)}
                    </button>
                    {isAddClassExpanded && (
                        <div className="mt-4 animate-in fade-in slide-in-from-top-2">
                            <AddClassForm 
                                selectedDay={selectedDay} 
                                onAddClass={handleAddClass}
                                onFormChange={setIsAddClassDirty}
                                onCancel={handleCancelAddClass}
                            />
                        </div>
                    )}
                 </div>
             </div>
        </div>

        {/* Info Modal for clipboard feedback */}
        <InfoModal
            isOpen={infoModal.isOpen}
            onClose={() => setInfoModal({ ...infoModal, isOpen: false })}
            title={infoModal.title}
            message={infoModal.message}
            variant={infoModal.variant}
        />

        {/* Confirm Modal for unsaved changes */}
        <ConfirmModal
            isOpen={unsavedChangesModal.isOpen}
            onClose={() => setUnsavedChangesModal({ isOpen: false, onConfirmAction: null })}
            onConfirm={() => {
                if (unsavedChangesModal.onConfirmAction) {
                    unsavedChangesModal.onConfirmAction();
                }
            }}
            title="Unsaved Changes"
            message="You have unsaved changes in the Add Class form. Discard changes?"
            variant="warning"
            confirmText="Discard"
            cancelText="Keep Editing"
        />
        </>
    );
};

export default AdminDashboard;
